import { ReactComponent as ACT } from "@/assets/images/config/australian-capital-territory.svg";
import { ReactComponent as NSW } from "@/assets/images/config/new-south-wales.svg";
import { ReactComponent as NT } from "@/assets/images/config/northern-territory.svg";
import { ReactComponent as QLD } from "@/assets/images/config/queensland.svg";
import { ReactComponent as SA } from "@/assets/images/config/south-australia.svg";
import { ReactComponent as TAS } from "@/assets/images/config/tasmania.svg";
import { ReactComponent as VIC } from "@/assets/images/config/victoria.svg";
import { ReactComponent as WA } from "@/assets/images/config/western-australia.svg";

/**
 * Region outlines for the pricing card grid, keyed by the slug of the region name.
 *
 * Only Australia has artwork. This registry replaces two byte-identical `switch (id)` blocks — one
 * in CustomRates, one in ContractPricingProfiles/StateRates — and, more usefully, it is what
 * `hasStateArtwork` derives the card-vs-table decision from.
 *
 * That decision used to be a `useTableViewForPricing` boolean on the country config, which was
 * really answering "have we drawn this country's regions yet?" — a fact about this folder, not about
 * a country. Deriving it means the next country needs no config change: with no artwork it gets the
 * table view, which is the better layout for unfamiliar regions anyway since it has a search box.
 */
const STATE_ARTWORK: Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  "australian-capital-territory": ACT,
  "new-south-wales": NSW,
  "northern-territory": NT,
  queensland: QLD,
  "south-australia": SA,
  tasmania: TAS,
  victoria: VIC,
  "western-australia": WA,
};

/** Country codes we ship region artwork for. */
const COUNTRIES_WITH_ARTWORK = ["AU"];

export const hasStateArtwork = (countryCode?: string): boolean => COUNTRIES_WITH_ARTWORK.includes((countryCode ?? "").toUpperCase());

/**
 * Renders a region's outline, or a plain placeholder block when we have no artwork for it — which
 * is what the US already gets, and what the twelve UK regions will get.
 */
export const StateArtwork = ({ slug, className, fallbackClassName }: { slug: string; className?: string; fallbackClassName?: string }) => {
  const Artwork = STATE_ARTWORK[slug];

  if (!Artwork) return <div className={fallbackClassName ?? className} />;

  return <Artwork className={className} />;
};
